import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { connect } from 'react-redux'
import {
  IFAvatar,
  IFButton,
  IFTable,
  IFText,
  IFSkeleton,
  IFGroupingComponent,
} from 'Components'
import { Colors } from 'Theme'
import Styles from './OperatorUserList.module.css'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import ReportIcon from '@mui/icons-material/Report'
import moment from 'moment'
import InfinityEnums from 'Enums/InfinityEnums'
import OperatorUserSelectors from 'Stores/OperatorUser/Selectors'
import OperatorUserActions from 'Stores/OperatorUser/Actions'
import { OperatorUserProfileModal } from 'Containers'

const OperatorUserList = ({
  operatorUsers,
  operatorUsersPagination,
  operatorUsersFilters,
  fetchOperatorUserListRequestState,
  fetchOperatorUserList,
  tablesHeight,
  deleteOperatorUserRequestState,
  updateOperatorUserRequestState,
  setClickedOperatorUser,
}) => {
  const { t } = useTranslation()
  const operatorUserProfile = useRef()

  const loadMoreData = () => {
    if (fetchOperatorUserListRequestState === RequestState.LOADING_MORE) return
    fetchOperatorUserList(operatorUsersFilters, operatorUsersPagination)
  }

  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchOperatorUserListRequestState === RequestState.ERROR_0_NETWORK ||
      fetchOperatorUserListRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchOperatorUserListRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchOperatorUserListRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchOperatorUserListRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchOperatorUserListRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    ) {
      setFailedRequest(true)
    }
    if (fetchOperatorUserListRequestState === RequestState.SUCCEEDED) {
      setFailedRequest(false)
    }
  }, [fetchOperatorUserListRequestState])
  const handleRetry = () => {
    fetchOperatorUserList(operatorUsersFilters, operatorUsersPagination)
  }

  const transformPermissions = (permissions) => {
    const transformedPermissions = permissions.map((permission) => {
      let lowerCase = permission.toLowerCase()
      lowerCase = lowerCase.replace(/_/g, ' ')
      return lowerCase.charAt(0).toUpperCase() + lowerCase.slice(1)
    })
    return transformedPermissions
  }

  const transformPickedPermissions = (permissions) => {
    let OperatorUserEnums = Object.keys(InfinityEnums.OperatorUserPermission)
    OperatorUserEnums = transformPermissions(OperatorUserEnums)
    const transformedPermissions = permissions.map(
      (number) => OperatorUserEnums[number - 1],
    )
    return transformedPermissions
  }

  const operatorUsersTableColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        enableEditing: false,
        size: 160,
        header:
          fetchOperatorUserListRequestState === RequestState.LOADING ||
          operatorUsersPagination === 0 ? (
            <div className={Styles.SkeletonDiv}>
              <IFSkeleton
                variant="text"
                width="110px"
                animation="none"
                height={20}
                className={Styles.Skeleton}
              />
            </div>
          ) : (
            <div>{t('OperatorUserPageContainer.Name')}</div>
          ),
        Cell: ({ row }) => {
          return row.index < operatorUsers.length ? (
            <div className={Styles.NameColumn}>
              <div>
                <IFAvatar
                  imageURL={row.original.avatarURL}
                  name={row.original.name}
                  size={40}
                />
              </div>
              <div>
                <IFText>{row.original.name}</IFText>
              </div>
            </div>
          ) : failedRequest ? (
            <div className={Styles.IconDiv}>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'email',
        size: 200,
        enableEditing: false,
        header:
          fetchOperatorUserListRequestState === RequestState.LOADING ||
          operatorUsersPagination === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('OperatorUserPageContainer.Email')}</IFText>
          ),
        Cell: ({ row }) => {
          return row.index < operatorUsers.length ? (
            <div className={Styles.parentFlexContainer}>
              <IFText className={Styles.TableText}>{row.original.email}</IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'createdOn',
        enableEditing: false,
        header:
          fetchOperatorUserListRequestState === RequestState.LOADING ||
          operatorUsersPagination === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <div>{t('OperatorUserPageContainer.CreatedAt')}</div>
          ),

        Cell: ({ row }) => {
          return row.index < operatorUsers.length ? (
            <div className={Styles.AdminColumnCreatedOn}>
              <IFText>
                {moment(row.original.createdAt).format('MMM DD, YYYY LTS')}
              </IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'permissions',
        size: 120,
        header:
          fetchOperatorUserListRequestState === RequestState.LOADING ||
          operatorUsersPagination === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('OperatorUserPageContainer.Permissions')}</IFText>
          ),
        muiTableBodyCellProps: {
          sx: {
            overflow: 'visible',
          },
        },

        Cell: ({ row }) => {
          return row.index < operatorUsers.length ? (
            <IFGroupingComponent
              componentText={transformPickedPermissions(
                row?.original?.permissions || [],
              )}
            />
          ) : failedRequest ? (
            <>
              <IFButton
                size="sm"
                color={Colors.red}
                text={t('IFTable.TryAgain')}
                onClick={handleRetry}
              />
            </>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
    ],
    [
      operatorUsers,
      fetchOperatorUserList,
      fetchOperatorUserListRequestState,
      failedRequest,
    ],
  )
  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )

  const handleRowClick = (rowData) => {
    operatorUserProfile.current.show(rowData)
  }
  useEffect(() => {
    if (deleteOperatorUserRequestState === RequestState.SUCCEEDED) {
      setClickedOperatorUser({})
      operatorUserProfile.current.dismiss()
    }
  }, [deleteOperatorUserRequestState])
  useEffect(() => {
    if (updateOperatorUserRequestState === RequestState.SUCCEEDED) {
      fetchOperatorUserList(operatorUsersFilters, operatorUsersPagination)
    }
  }, [updateOperatorUserRequestState])

  return (
    <div>
      {((fetchOperatorUserListRequestState !== RequestState.SUCCEEDED &&
        !operatorUsersPagination) ||
        operatorUsers) && (
        <>
          <IFTable
            columns={operatorUsersTableColumns}
            data={operatorUsers}
            fetchMoreOnBottomReached={fetchMoreOnBottomReached}
            paginationOffset={operatorUsersPagination}
            filters={operatorUsersFilters}
            showSkeleton={
              fetchOperatorUserListRequestState === RequestState.LOADING ||
              (fetchOperatorUserListRequestState !== RequestState.SUCCEEDED &&
                operatorUsersPagination === 0)
            }
            tableMaxHeight={tablesHeight}
            tableSetHeight={'100%'}
            tableRequestState={fetchOperatorUserListRequestState}
            onRowClick={handleRowClick}
          />
        </>
      )}
      <OperatorUserProfileModal
        ref={operatorUserProfile}
        setClickedOperatorUser={setClickedOperatorUser}
      />
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchOperatorUserList: (filter, offset) =>
      dispatch(OperatorUserActions.fetchOperatorUserList(filter, offset)),
    setClickedOperatorUser: (rowData) =>
      dispatch(OperatorUserActions.setClickedOperatorUser(rowData)),
  }
}
const mapStateToProps = (state) => ({
  operatorUsers: OperatorUserSelectors.getOperatorUsers(state),
  fetchOperatorUserListRequestState:
    OperatorUserSelectors.getFetchOperatorUserListRequestState(state),
  operatorUsersPagination:
    OperatorUserSelectors.getOperatorUsersPagination(state),
  operatorUsersFilters: OperatorUserSelectors.getOperatorUsersFilters(state),
  deleteOperatorUserRequestState:
    OperatorUserSelectors.getDeleteOperatorUserRequestState(state),
  updateOperatorUserRequestState:
    OperatorUserSelectors.getUpdateOperatorUserRequestState(state),
})
OperatorUserList.propTypes = {
  fetchOperatorUserList: PropTypes.func,
  tablesHeight: PropTypes.number,
  operatorUsersPagination: PropTypes.number,
  operatorUsersFilters: PropTypes.object,
  fetchOperatorUserListRequestState: PropTypes.number,
  operatorUsers: PropTypes.array,
}
export default connect(mapStateToProps, mapDispatchToProps)(OperatorUserList)
